import React, { useState } from 'react';
import { ArrowLeft, Heart, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Kiss10 = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const kissEmojis = [
    '💋', '😘', '😚', '😗', '😙', '💏', '👩‍❤️‍💋‍👨', '👨‍❤️‍💋‍👨', '👩‍❤️‍💋‍👩', '❤️'
  ];

  const kisses = Array(10)
    .fill(null)
    .map(() => kissEmojis[Math.floor(Math.random() * kissEmojis.length)])
    .join('');

  const handleCopy = () => {
    navigator.clipboard.writeText(kisses);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Rich snippets for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `10 Kiss Emojis Generator [${currentYear}]`,
    "description": `Generate and copy 10 beautiful kiss emojis 💋. Updated for ${currentYear} with new kiss emojis and designs.`,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1583",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I copy kiss emojis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Simply click the 'Copy 10 Kisses' button and all kiss emojis will be copied to your clipboard. You can then paste them anywhere using Ctrl+V (Windows) or Cmd+V (Mac)."
        }
      },
      {
        "@type": "Question",
        "name": "Do these kiss emojis work everywhere?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Our kiss emojis work on all major platforms including Instagram, Facebook, WhatsApp, Twitter, TikTok, and more. They're compatible with both mobile and desktop devices."
        }
      },
      {
        "@type": "Question",
        "name": "What do different kiss emojis mean?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Different kiss emojis convey various meanings: 💋 for romantic kiss, 😘 for blowing kiss, 😚 for innocent kiss, 😗 for simple kiss, 😙 for happy kiss, and 💏 for couple kissing."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>10 Kiss Emojis 💋 Copy and Paste [{currentYear}] 😘 Free Kiss Collection</title>
        <meta name="description" content={`【INSTANT COPY】 Get 10 kiss emojis! 💋 Perfect mix of romantic kisses 😘 One-click copy & paste 💋 Works everywhere! Updated for ${currentYear} 😚 Free kiss emojis for Instagram, WhatsApp & more!`} />
        <link rel="canonical" href="https://emojicollection.net/emoji/kiss-10" />
        
        <meta property="og:title" content={`10 Kiss Emojis 💋 Copy and Paste [${currentYear}] 😘 Free Kiss Collection`} />
        <meta property="og:description" content={`Get 10 kiss emojis! 💋 Perfect mix of romantic kisses 😘 One-click copy & paste 💋 Works everywhere! Updated for ${currentYear}`} />
        <meta property="og:url" content="https://emojicollection.net/emoji/kiss-10" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`10 Kiss Emojis 💋 Copy and Paste [${currentYear}] 😘 Free Kiss Collection`} />
        <meta name="twitter:description" content={`Get 10 kiss emojis! 💋 Perfect mix of romantic kisses 😘 One-click copy & paste 💋 Works everywhere! Updated for ${currentYear}`} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="10 kiss emojis, kiss emoji copy paste, kiss emojis, romantic emojis, love emoji, kiss symbols, instagram kisses, whatsapp kisses" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?auto=format&fit=crop&w=1200&q=80"
                alt="Express love with beautiful kiss emojis"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-pink-500 to-red-500 text-white mb-4">
                <Heart className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                10 Kiss Emojis 💋 Copy and Paste [{currentYear}] 😘
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get instant access to 10 beautiful kiss emojis! Perfect for expressing love and affection. Each emoji is carefully selected to help convey your feelings. Updated for {currentYear} with the latest emojis. One-click copy and paste feature!
              </p>
            </header>

            <section className="max-w-2xl mx-auto mb-12">
              <div className="bg-gradient-to-r from-pink-50 to-red-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  💋 Copy 10 Kiss Emojis Instantly
                </h2>
                <div className="mb-4 overflow-auto max-h-96 text-left break-words text-2xl">
                  {kisses}
                </div>
                <button
                  onClick={handleCopy}
                  className={`inline-flex items-center px-6 py-3 rounded-lg text-lg font-medium transition-all transform hover:scale-105 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={20} />
                      Copied 10 Kisses!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={20} />
                      Copy 10 Kisses
                    </>
                  )}
                </button>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold mb-4">✨ Kiss Emoji Types & Meanings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">Kiss Variations</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">💋 Kiss Mark - Romantic kiss</li>
                    <li className="flex items-center">😘 Face Blowing Kiss - Playful affection</li>
                    <li className="flex items-center">😚 Kissing Face - Sweet, innocent kiss</li>
                    <li className="flex items-center">😗 Kissing Face - Simple kiss</li>
                    <li className="flex items-center">😙 Kissing Face with Smiling Eyes - Happy kiss</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-4">Features</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center">✨ 10 beautiful kiss emojis</li>
                    <li className="flex items-center">💫 Perfect emoji mix</li>
                    <li className="flex items-center">📱 Works on all devices</li>
                    <li className="flex items-center">🔄 Random selection</li>
                    <li className="flex items-center">📋 One-click copy feature</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">📱 Perfect For</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Romance</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Love messages</li>
                    <li>Valentine's Day</li>
                    <li>Anniversaries</li>
                    <li>Date nights</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Social Media</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Instagram posts</li>
                    <li>Facebook updates</li>
                    <li>Twitter messages</li>
                    <li>TikTok captions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Messaging</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>WhatsApp</li>
                    <li>Messenger</li>
                    <li>iMessage</li>
                    <li>SMS</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Why use kiss emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Kiss emojis are perfect for expressing affection, love, and romance. They add a playful and romantic touch to your messages and social media posts.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Do these kisses work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! These kiss emojis are universal and work on all major platforms including Instagram, Facebook, WhatsApp, and more. They're compatible with both mobile and desktop devices.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Are these kisses free to use?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Absolutely! Our kiss emojis are completely free to use for both personal and commercial purposes. No attribution required.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default Kiss10;